import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    Brain,
    TrendingUp,
    TrendingDown,
    Package,
    ShoppingCart,
    AlertTriangle,
    CheckCircle2,
    RefreshCw,
    Sparkles,
    BarChart3,
    Send,
    MessageSquare,
    Lightbulb,
    Target,
    Clock,
    DollarSign,
    ChefHat,
    Loader2
} from 'lucide-react';

const AIPredictionsPage = () => {
    const { api, user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('dashboard');
    
    // Data states
    const [dashboard, setDashboard] = useState(null);
    const [salesForecast, setSalesForecast] = useState(null);
    const [stockRecommendations, setStockRecommendations] = useState(null);
    const [menuAnalysis, setMenuAnalysis] = useState(null);
    
    // Chat state
    const [chatMessage, setChatMessage] = useState('');
    const [chatHistory, setChatHistory] = useState([]);
    const [chatLoading, setChatLoading] = useState(false);
    const [chatContext, setChatContext] = useState('general');
    
    // Loading states for individual sections
    const [loadingSales, setLoadingSales] = useState(false);
    const [loadingStock, setLoadingStock] = useState(false);
    const [loadingMenu, setLoadingMenu] = useState(false);
    
    const canAccess = user?.role === 'ADMIN';
    
    const fetchDashboard = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api().get('/ai/dashboard');
            setDashboard(res.data);
        } catch (error) {
            toast.error('Erreur lors du chargement du tableau de bord IA');
        } finally {
            setLoading(false);
        }
    }, [api]);
    
    const fetchSalesForecast = async (period = 'week') => {
        try {
            setLoadingSales(true);
            const res = await api().get(`/ai/sales-forecast?period=${period}`);
            setSalesForecast(res.data);
        } catch (error) {
            toast.error('Erreur lors de la prévision des ventes');
        } finally {
            setLoadingSales(false);
        }
    };
    
    const fetchStockRecommendations = async () => {
        try {
            setLoadingStock(true);
            const res = await api().get('/ai/stock-recommendations');
            setStockRecommendations(res.data);
        } catch (error) {
            toast.error('Erreur lors de l\'analyse du stock');
        } finally {
            setLoadingStock(false);
        }
    };
    
    const fetchMenuAnalysis = async () => {
        try {
            setLoadingMenu(true);
            const res = await api().get('/ai/menu-analysis');
            setMenuAnalysis(res.data);
        } catch (error) {
            toast.error('Erreur lors de l\'analyse du menu');
        } finally {
            setLoadingMenu(false);
        }
    };
    
    const sendChatMessage = async () => {
        if (!chatMessage.trim()) return;
        
        const userMsg = chatMessage;
        setChatMessage('');
        setChatHistory(prev => [...prev, { role: 'user', content: userMsg }]);
        setChatLoading(true);
        
        try {
            const res = await api().post(`/ai/chat?message=${encodeURIComponent(userMsg)}&context=${chatContext}`);
            setChatHistory(prev => [...prev, { role: 'assistant', content: res.data.response }]);
        } catch (error) {
            setChatHistory(prev => [...prev, { role: 'assistant', content: 'Désolé, une erreur s\'est produite. Veuillez réessayer.' }]);
        } finally {
            setChatLoading(false);
        }
    };
    
    useEffect(() => {
        if (canAccess) {
            fetchDashboard();
        }
    }, [canAccess, fetchDashboard]);
    
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('fr-FR').format(value || 0) + ' FCFA';
    };
    
    const getUrgencyColor = (urgency) => {
        switch (urgency) {
            case 'critical': return 'bg-red-100 text-red-700 border-red-300';
            case 'high': return 'bg-orange-100 text-orange-700 border-orange-300';
            case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
            default: return 'bg-green-100 text-green-700 border-green-300';
        }
    };
    
    const getConfidenceColor = (confidence) => {
        switch (confidence) {
            case 'élevée': return 'text-green-600';
            case 'moyenne': return 'text-yellow-600';
            default: return 'text-red-600';
        }
    };
    
    if (!canAccess) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Card className="max-w-md">
                    <CardContent className="p-8 text-center">
                        <Brain className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                        <h2 className="text-xl font-bold mb-2">Accès Restreint</h2>
                        <p className="text-muted-foreground">
                            Le tableau de bord IA est réservé à la direction.
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }
    
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary mb-4" />
                    <p className="text-muted-foreground">Analyse en cours...</p>
                </div>
            </div>
        );
    }
    
    return (
        <div className="space-y-6" data-testid="ai-predictions-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2">
                        <Brain className="w-8 h-8 text-purple-600" />
                        <h1 className="text-2xl font-bold text-foreground">Assistant IA</h1>
                        <Badge className="bg-purple-100 text-purple-700">GPT-5.2</Badge>
                    </div>
                    <p className="text-muted-foreground mt-1">Prévisions et recommandations intelligentes</p>
                </div>
                <Button variant="outline" onClick={fetchDashboard}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Actualiser
                </Button>
            </div>
            
            {/* Quick Stats */}
            {dashboard?.stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-blue-600 font-medium">Ventes Semaine</p>
                                    <p className="text-xl font-bold text-blue-700">{formatCurrency(dashboard.stats.total_sales_week)}</p>
                                </div>
                                <TrendingUp className="w-8 h-8 text-blue-500" />
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-green-600 font-medium">Jours de Ventes</p>
                                    <p className="text-xl font-bold text-green-700">{dashboard.stats.sales_days}</p>
                                </div>
                                <BarChart3 className="w-8 h-8 text-green-500" />
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card className={`bg-gradient-to-br ${dashboard.stats.low_stock_count > 5 ? 'from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 border-red-200' : 'from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 border-yellow-200'}`}>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className={`text-sm font-medium ${dashboard.stats.low_stock_count > 5 ? 'text-red-600' : 'text-yellow-600'}`}>Stock Faible</p>
                                    <p className={`text-xl font-bold ${dashboard.stats.low_stock_count > 5 ? 'text-red-700' : 'text-yellow-700'}`}>{dashboard.stats.low_stock_count}</p>
                                </div>
                                <Package className={`w-8 h-8 ${dashboard.stats.low_stock_count > 5 ? 'text-red-500' : 'text-yellow-500'}`} />
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-purple-600 font-medium">Produits Actifs</p>
                                    <p className="text-xl font-bold text-purple-700">{dashboard.stats.total_products}</p>
                                </div>
                                <Sparkles className="w-8 h-8 text-purple-500" />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
            
            {/* AI Summary */}
            {dashboard?.ai_summary && (
                <Card className="border-purple-200 bg-gradient-to-r from-purple-50/50 to-pink-50/50 dark:from-purple-900/10 dark:to-pink-900/10">
                    <CardHeader className="pb-2">
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <Lightbulb className="w-5 h-5 text-yellow-500" />
                            Résumé IA
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-foreground whitespace-pre-line">{dashboard.ai_summary}</p>
                    </CardContent>
                </Card>
            )}
            
            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid grid-cols-4 w-full max-w-2xl">
                    <TabsTrigger value="dashboard">
                        <BarChart3 className="w-4 h-4 mr-2" />
                        Prévisions
                    </TabsTrigger>
                    <TabsTrigger value="stock">
                        <Package className="w-4 h-4 mr-2" />
                        Stock
                    </TabsTrigger>
                    <TabsTrigger value="menu">
                        <ChefHat className="w-4 h-4 mr-2" />
                        Menu
                    </TabsTrigger>
                    <TabsTrigger value="chat">
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Chat
                    </TabsTrigger>
                </TabsList>
                
                {/* Sales Forecast Tab */}
                <TabsContent value="dashboard" className="mt-4">
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <TrendingUp className="w-5 h-5 text-blue-600" />
                                        Prévisions des Ventes
                                    </CardTitle>
                                    <CardDescription>Analyse et prédictions basées sur l'historique</CardDescription>
                                </div>
                                <Button 
                                    onClick={() => fetchSalesForecast('week')} 
                                    disabled={loadingSales}
                                    data-testid="generate-forecast-btn"
                                >
                                    {loadingSales ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Sparkles className="w-4 h-4 mr-2" />
                                    )}
                                    Générer Prévision
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {!salesForecast ? (
                                <div className="text-center py-12 text-muted-foreground">
                                    <Target className="w-16 h-16 mx-auto mb-4 opacity-50" />
                                    <p>Cliquez sur "Générer Prévision" pour analyser vos ventes</p>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {/* Forecast Summary */}
                                    {salesForecast.forecast && (
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <Card className="bg-blue-50 dark:bg-blue-900/20">
                                                <CardContent className="p-4 text-center">
                                                    <p className="text-sm text-blue-600 mb-1">Prévision Semaine Prochaine</p>
                                                    <p className="text-2xl font-bold text-blue-700">
                                                        {formatCurrency(salesForecast.forecast.forecast_next_week)}
                                                    </p>
                                                    <Badge className={`mt-2 ${getConfidenceColor(salesForecast.forecast.confidence)}`}>
                                                        Confiance: {salesForecast.forecast.confidence}
                                                    </Badge>
                                                </CardContent>
                                            </Card>
                                            
                                            <Card className={`${salesForecast.forecast.trend === 'hausse' ? 'bg-green-50 dark:bg-green-900/20' : salesForecast.forecast.trend === 'baisse' ? 'bg-red-50 dark:bg-red-900/20' : 'bg-gray-50 dark:bg-gray-900/20'}`}>
                                                <CardContent className="p-4 text-center">
                                                    <p className="text-sm text-muted-foreground mb-1">Tendance</p>
                                                    <div className="flex items-center justify-center gap-2">
                                                        {salesForecast.forecast.trend === 'hausse' ? (
                                                            <TrendingUp className="w-6 h-6 text-green-600" />
                                                        ) : salesForecast.forecast.trend === 'baisse' ? (
                                                            <TrendingDown className="w-6 h-6 text-red-600" />
                                                        ) : (
                                                            <BarChart3 className="w-6 h-6 text-gray-600" />
                                                        )}
                                                        <span className="text-xl font-bold capitalize">{salesForecast.forecast.trend}</span>
                                                    </div>
                                                    {salesForecast.historical_data?.trend_percentage && (
                                                        <p className="text-sm mt-1">
                                                            {salesForecast.historical_data.trend_percentage > 0 ? '+' : ''}
                                                            {salesForecast.historical_data.trend_percentage}% vs semaine précédente
                                                        </p>
                                                    )}
                                                </CardContent>
                                            </Card>
                                            
                                            <Card className="bg-purple-50 dark:bg-purple-900/20">
                                                <CardContent className="p-4 text-center">
                                                    <p className="text-sm text-purple-600 mb-1">Meilleurs Jours</p>
                                                    <div className="flex flex-wrap justify-center gap-1 mt-2">
                                                        {salesForecast.forecast.best_days?.map((day, idx) => (
                                                            <Badge key={idx} variant="outline" className="border-purple-300">
                                                                {day}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </div>
                                    )}
                                    
                                    {/* Analysis */}
                                    {salesForecast.forecast?.analysis && (
                                        <Card>
                                            <CardContent className="p-4">
                                                <p className="text-foreground">{salesForecast.forecast.analysis}</p>
                                            </CardContent>
                                        </Card>
                                    )}
                                    
                                    {/* Recommendations */}
                                    {salesForecast.forecast?.recommendations?.length > 0 && (
                                        <div>
                                            <h3 className="font-semibold mb-3 flex items-center gap-2">
                                                <Lightbulb className="w-5 h-5 text-yellow-500" />
                                                Recommandations
                                            </h3>
                                            <div className="space-y-2">
                                                {salesForecast.forecast.recommendations.map((rec, idx) => (
                                                    <div key={idx} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                                                        <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5" />
                                                        <p>{rec}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
                
                {/* Stock Recommendations Tab */}
                <TabsContent value="stock" className="mt-4">
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <Package className="w-5 h-5 text-orange-600" />
                                        Optimisation du Stock
                                    </CardTitle>
                                    <CardDescription>Recommandations de réapprovisionnement intelligent</CardDescription>
                                </div>
                                <Button 
                                    onClick={fetchStockRecommendations} 
                                    disabled={loadingStock}
                                    data-testid="analyze-stock-btn"
                                >
                                    {loadingStock ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Sparkles className="w-4 h-4 mr-2" />
                                    )}
                                    Analyser Stock
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {!stockRecommendations ? (
                                <div className="text-center py-12 text-muted-foreground">
                                    <ShoppingCart className="w-16 h-16 mx-auto mb-4 opacity-50" />
                                    <p>Cliquez sur "Analyser Stock" pour obtenir des recommandations</p>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {/* Stock Summary */}
                                    {stockRecommendations.stock_summary && (
                                        <div className="grid grid-cols-3 gap-4">
                                            <Card>
                                                <CardContent className="p-4 text-center">
                                                    <p className="text-2xl font-bold">{stockRecommendations.stock_summary.total_products}</p>
                                                    <p className="text-sm text-muted-foreground">Produits Total</p>
                                                </CardContent>
                                            </Card>
                                            <Card className="bg-yellow-50 dark:bg-yellow-900/20">
                                                <CardContent className="p-4 text-center">
                                                    <p className="text-2xl font-bold text-yellow-700">{stockRecommendations.stock_summary.low_stock_count}</p>
                                                    <p className="text-sm text-muted-foreground">Stock Faible</p>
                                                </CardContent>
                                            </Card>
                                            <Card className="bg-red-50 dark:bg-red-900/20">
                                                <CardContent className="p-4 text-center">
                                                    <p className="text-2xl font-bold text-red-700">{stockRecommendations.stock_summary.critical_count || 0}</p>
                                                    <p className="text-sm text-muted-foreground">Critique</p>
                                                </CardContent>
                                            </Card>
                                        </div>
                                    )}
                                    
                                    {/* AI Recommendations */}
                                    {stockRecommendations.ai_recommendations && (
                                        <>
                                            {stockRecommendations.ai_recommendations.urgency_level && (
                                                <div className={`p-4 rounded-lg border ${getUrgencyColor(stockRecommendations.ai_recommendations.urgency_level)}`}>
                                                    <div className="flex items-center gap-2">
                                                        <AlertTriangle className="w-5 h-5" />
                                                        <span className="font-semibold">
                                                            Niveau d'urgence: {stockRecommendations.ai_recommendations.urgency_level}
                                                        </span>
                                                    </div>
                                                    {stockRecommendations.ai_recommendations.general_advice && (
                                                        <p className="mt-2">{stockRecommendations.ai_recommendations.general_advice}</p>
                                                    )}
                                                </div>
                                            )}
                                            
                                            {stockRecommendations.ai_recommendations.recommendations?.length > 0 && (
                                                <div>
                                                    <h3 className="font-semibold mb-3">Actions Recommandées</h3>
                                                    <div className="space-y-3">
                                                        {stockRecommendations.ai_recommendations.recommendations.map((rec, idx) => (
                                                            <div key={idx} className="p-4 border rounded-lg bg-muted/20">
                                                                <div className="flex items-start justify-between">
                                                                    <div>
                                                                        <span className="font-medium">{rec.product}</span>
                                                                        <Badge className="ml-2" variant={rec.priority === 'haute' ? 'destructive' : 'outline'}>
                                                                            {rec.priority}
                                                                        </Badge>
                                                                    </div>
                                                                    <Badge variant="outline">{rec.action}</Badge>
                                                                </div>
                                                                <p className="text-sm text-muted-foreground mt-2">{rec.reason}</p>
                                                                {rec.quantity_suggested && (
                                                                    <p className="text-sm font-medium mt-1">
                                                                        Quantité suggérée: {rec.quantity_suggested}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {stockRecommendations.ai_recommendations.estimated_budget && (
                                                <Card className="bg-green-50 dark:bg-green-900/20">
                                                    <CardContent className="p-4">
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-sm text-green-600">Budget Estimé pour Réapprovisionnement</span>
                                                            <span className="text-xl font-bold text-green-700">
                                                                {formatCurrency(stockRecommendations.ai_recommendations.estimated_budget)}
                                                            </span>
                                                        </div>
                                                    </CardContent>
                                                </Card>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
                
                {/* Menu Analysis Tab */}
                <TabsContent value="menu" className="mt-4">
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <ChefHat className="w-5 h-5 text-green-600" />
                                        Analyse du Menu
                                    </CardTitle>
                                    <CardDescription>Performance des plats et optimisation du menu</CardDescription>
                                </div>
                                <Button 
                                    onClick={fetchMenuAnalysis} 
                                    disabled={loadingMenu}
                                    data-testid="analyze-menu-btn"
                                >
                                    {loadingMenu ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Sparkles className="w-4 h-4 mr-2" />
                                    )}
                                    Analyser Menu
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {!menuAnalysis ? (
                                <div className="text-center py-12 text-muted-foreground">
                                    <ChefHat className="w-16 h-16 mx-auto mb-4 opacity-50" />
                                    <p>Cliquez sur "Analyser Menu" pour évaluer vos plats</p>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {/* Menu Summary */}
                                    {menuAnalysis.menu_summary && (
                                        <div className="grid grid-cols-3 gap-4">
                                            <Card>
                                                <CardContent className="p-4 text-center">
                                                    <p className="text-2xl font-bold">{menuAnalysis.menu_summary.total_items}</p>
                                                    <p className="text-sm text-muted-foreground">Articles au Menu</p>
                                                </CardContent>
                                            </Card>
                                            <Card className="bg-green-50 dark:bg-green-900/20">
                                                <CardContent className="p-4 text-center">
                                                    <p className="text-2xl font-bold text-green-700">{menuAnalysis.menu_summary.items_with_sales}</p>
                                                    <p className="text-sm text-muted-foreground">Avec Ventes</p>
                                                </CardContent>
                                            </Card>
                                            <Card className="bg-blue-50 dark:bg-blue-900/20">
                                                <CardContent className="p-4 text-center">
                                                    <p className="text-lg font-bold text-blue-700">{formatCurrency(menuAnalysis.menu_summary.total_revenue)}</p>
                                                    <p className="text-sm text-muted-foreground">Revenus 30j</p>
                                                </CardContent>
                                            </Card>
                                        </div>
                                    )}
                                    
                                    {/* AI Analysis */}
                                    {menuAnalysis.ai_analysis && (
                                        <>
                                            {/* Top Performers */}
                                            {menuAnalysis.ai_analysis.top_performers?.length > 0 && (
                                                <div>
                                                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                                                        <TrendingUp className="w-5 h-5 text-green-500" />
                                                        Meilleurs Performers
                                                    </h3>
                                                    <div className="flex flex-wrap gap-2">
                                                        {menuAnalysis.ai_analysis.top_performers.map((item, idx) => (
                                                            <Badge key={idx} className="bg-green-100 text-green-700 px-3 py-1">
                                                                {item}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {/* Underperformers */}
                                            {menuAnalysis.ai_analysis.underperformers?.length > 0 && (
                                                <div>
                                                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                                                        <TrendingDown className="w-5 h-5 text-red-500" />
                                                        À Améliorer
                                                    </h3>
                                                    <div className="flex flex-wrap gap-2">
                                                        {menuAnalysis.ai_analysis.underperformers.map((item, idx) => (
                                                            <Badge key={idx} className="bg-red-100 text-red-700 px-3 py-1">
                                                                {item}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {/* Recommendations */}
                                            {menuAnalysis.ai_analysis.recommendations?.length > 0 && (
                                                <div>
                                                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                                                        <Lightbulb className="w-5 h-5 text-yellow-500" />
                                                        Recommandations
                                                    </h3>
                                                    <div className="space-y-3">
                                                        {menuAnalysis.ai_analysis.recommendations.map((rec, idx) => (
                                                            <div key={idx} className="p-4 border rounded-lg bg-muted/20">
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    <Badge variant="outline">{rec.type}</Badge>
                                                                    <span className="font-medium">{rec.item}</span>
                                                                </div>
                                                                <p className="text-sm">{rec.suggestion}</p>
                                                                {rec.expected_impact && (
                                                                    <p className="text-xs text-muted-foreground mt-1">
                                                                        Impact attendu: {rec.expected_impact}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {/* Summary */}
                                            {menuAnalysis.ai_analysis.summary && (
                                                <Card className="bg-purple-50 dark:bg-purple-900/20">
                                                    <CardContent className="p-4">
                                                        <p className="text-foreground">{menuAnalysis.ai_analysis.summary}</p>
                                                    </CardContent>
                                                </Card>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
                
                {/* Chat Tab */}
                <TabsContent value="chat" className="mt-4">
                    <Card className="h-[600px] flex flex-col">
                        <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                                <CardTitle className="flex items-center gap-2">
                                    <MessageSquare className="w-5 h-5 text-purple-600" />
                                    Assistant IA
                                </CardTitle>
                                <Select value={chatContext} onValueChange={setChatContext}>
                                    <SelectTrigger className="w-40">
                                        <SelectValue placeholder="Contexte" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="general">Général</SelectItem>
                                        <SelectItem value="sales">Ventes</SelectItem>
                                        <SelectItem value="stock">Stock</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <CardDescription>Posez vos questions sur la gestion du restaurant</CardDescription>
                        </CardHeader>
                        <CardContent className="flex-1 flex flex-col overflow-hidden">
                            {/* Chat History */}
                            <div className="flex-1 overflow-y-auto space-y-4 mb-4 p-2">
                                {chatHistory.length === 0 ? (
                                    <div className="text-center py-8 text-muted-foreground">
                                        <Brain className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                        <p>Commencez une conversation avec l'assistant IA</p>
                                        <p className="text-sm mt-2">Exemples de questions:</p>
                                        <div className="flex flex-wrap justify-center gap-2 mt-2">
                                            <Badge variant="outline" className="cursor-pointer" onClick={() => setChatMessage("Quels plats devrais-je promouvoir cette semaine?")}>
                                                Plats à promouvoir
                                            </Badge>
                                            <Badge variant="outline" className="cursor-pointer" onClick={() => setChatMessage("Comment réduire le gaspillage alimentaire?")}>
                                                Réduire le gaspillage
                                            </Badge>
                                            <Badge variant="outline" className="cursor-pointer" onClick={() => setChatMessage("Analyse mes marges bénéficiaires")}>
                                                Analyser les marges
                                            </Badge>
                                        </div>
                                    </div>
                                ) : (
                                    chatHistory.map((msg, idx) => (
                                        <div 
                                            key={idx} 
                                            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                        >
                                            <div 
                                                className={`max-w-[80%] p-3 rounded-lg ${
                                                    msg.role === 'user' 
                                                        ? 'bg-primary text-primary-foreground' 
                                                        : 'bg-muted'
                                                }`}
                                            >
                                                <p className="whitespace-pre-line">{msg.content}</p>
                                            </div>
                                        </div>
                                    ))
                                )}
                                {chatLoading && (
                                    <div className="flex justify-start">
                                        <div className="bg-muted p-3 rounded-lg">
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            {/* Chat Input */}
                            <div className="flex gap-2">
                                <Textarea
                                    placeholder="Posez votre question..."
                                    value={chatMessage}
                                    onChange={(e) => setChatMessage(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            sendChatMessage();
                                        }
                                    }}
                                    rows={2}
                                    className="resize-none"
                                    data-testid="ai-chat-input"
                                />
                                <Button 
                                    onClick={sendChatMessage} 
                                    disabled={chatLoading || !chatMessage.trim()}
                                    data-testid="send-chat-btn"
                                >
                                    <Send className="w-4 h-4" />
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default AIPredictionsPage;
